import { BaseApi } from "@portal/api/apiBase";
import { ParticipantType, Order, EventRegistration } from '../types';
import { ReservationInfo } from '../types/reservation';

export class RegistrationApi extends BaseApi {
    constructor() {
        // Base URL remains /api/portal
        super('/api/portal');
    }

    // Initiate registration for an event
    async startRegistration(eventSlug: string): Promise<Order> {
        console.log(`Starting registration via API for ${eventSlug}`);
        try {
            // Make POST request to the new endpoint - the new post method handles JSON:API deserialization
            const order = await this.post<Order>(`/events/${eventSlug}/registrations`);
            console.log('Order created via API:', order);
            return order;
        } catch (error) {
            console.error(`API Error starting registration for ${eventSlug}:`, error);
            // Re-throw the error so React Query can handle it
            throw error;
        }
    }

    // Fetch available participant types for an event
    async getParticipantTypes(eventSlug: string): Promise<ParticipantType[]> {
        console.log(`Fetching participant types for ${eventSlug}`);
        try {
            // The BaseApi class will handle JSON:API deserialization automatically
            const participantTypes = await this.get<ParticipantType[]>(`/events/${eventSlug}/participant_types`);
            console.log('Participant types fetched:', participantTypes);
            return participantTypes;
        } catch (error) {
            console.error(`API Error fetching participant types for ${eventSlug}:`, error);
            throw error;
        }
    }

    // Set participant type for an order
    async setParticipantType(orderToken: string, participantTypeId: number): Promise<Order> {
        console.log(`Setting participant type ${participantTypeId} for order ${orderToken}`);
        try {
            // The BaseApi will handle snake_case conversion automatically
            const order = await this.put<Order>(
                `/orders/${orderToken}/participant_type`, 
                { participantTypeId }
            );
            console.log('Order updated with participant type:', order);
            return order;
        } catch (error) {
            console.error(`API Error setting participant type for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Get order details
    async getOrder(orderToken: string): Promise<Order> {
        console.log(`Fetching order ${orderToken}`);
        try {
            const order = await this.get<Order>(`/orders/${orderToken}`);
            console.log('Order fetched:', order);
            return order;
        } catch (error) {
            console.error(`API Error fetching order ${orderToken}:`, error);
            throw error;
        }
    }

    // Start registration with pre-selected participant type
    async startRegistrationWithParticipantType(
        eventSlug: string, 
        participantTypeSlug: string
    ): Promise<Order> {
        console.log(`Starting registration for ${eventSlug} with participant type ${participantTypeSlug}`);
        try {
            const order = await this.post<Order>(
                `/events/${eventSlug}/registrations`,
                { participantTypeSlug }
            );
            console.log('Order created with participant type:', order);
            return order;
        } catch (error: any) {
            console.error(`API Error starting registration with participant type:`, error);
            // Re-throw with additional context
            throw {
                status: error.status,
                message: error.message || 'Registration failed',
                ...error
            };
        }
    }

    // Get available tickets for the current order
    async getAvailableTickets(eventSlug: string, orderToken: string): Promise<any[]> {
        console.log(`Fetching available tickets for order ${orderToken}`);
        try {
            const tickets = await this.get<any[]>(`/events/${eventSlug}/orders/${orderToken}/available_tickets`);
            console.log('Available tickets fetched:', tickets);
            return tickets;
        } catch (error) {
            console.error(`API Error fetching tickets:`, error);
            throw error;
        }
    }

    // Get registration survey
    async getRegistrationSurvey(eventSlug: string, orderToken: string): Promise<any> {
        console.log(`Fetching registration survey for order ${orderToken}`);
        try {
            const survey = await this.get<any>(`/events/${eventSlug}/registration_survey?order_token=${orderToken}`);
            console.log('Registration survey fetched:', survey);
            return survey;
        } catch (error) {
            console.error(`API Error fetching registration survey:`, error);
            throw error;
        }
    }

    // Proceed to payment from summary page
    async proceedToPayment(orderToken: string): Promise<Order> {
        console.log(`Proceeding to payment for order ${orderToken}`);
        try {
            const order = await this.post<Order>(
                `/orders/${orderToken}/proceed_to_payment`, 
                {}
            );
            console.log('Proceed to payment response:', order);
            return order;
        } catch (error) {
            console.error(`API Error proceeding to payment for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Update payment method and details
    async updatePaymentMethod(orderToken: string, paymentData: any): Promise<Order> {
        console.log(`Updating payment method for order ${orderToken}`, paymentData);
        try {
            const order = await this.put<Order>(
                `/orders/${orderToken}/update_payment_method`, 
                paymentData
            );
            console.log('Update payment method response:', order);
            return order;
        } catch (error) {
            console.error(`API Error updating payment method for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Create payment intent
    async createPaymentIntent(orderToken: string, params: {
        paymentMethodType: string;
        savePaymentMethod?: boolean;
        metadata?: Record<string, any>;
    }): Promise<{
        clientSecret: string;
        amount: number;
        currency: string;
        paymentMethodTypes: string[];
        status: string;
        stripeAccountId?: string;
    }> {
        console.log(`Creating payment intent for order ${orderToken}:`, params);
        try {
            const response = await this.post<any>(`/orders/${orderToken}/create_payment_intent`, {
                payment_method_type: params.paymentMethodType,
                save_payment_method: params.savePaymentMethod,
                metadata: params.metadata
            });
            console.log('Payment intent created:', response);
            return response;
        } catch (error) {
            console.error(`API Error creating payment intent for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Confirm payment
    async confirmPayment(orderToken: string, params: {
        paymentIntentId: string;
        paymentMethodId?: string;
    }): Promise<Order> {
        console.log(`Confirming payment for order ${orderToken}:`, params);
        try {
            const order = await this.post<Order>(`/orders/${orderToken}/confirm_payment`, {
                payment_intent_id: params.paymentIntentId,
                payment_method_id: params.paymentMethodId
            });
            console.log('Payment confirmed, order:', order);
            return order;
        } catch (error) {
            console.error(`API Error confirming payment for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Update order email
    async updateEmail(orderToken: string, email: string): Promise<Order> {
        console.log(`Updating email for order ${orderToken} to:`, email);
        try {
            const order = await this.put<Order>(`/orders/${orderToken}/update_email`, { email });
            console.log('Email updated, order:', order);
            return order;
        } catch (error) {
            console.error(`API Error updating email for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Navigate back to survey from summary
    async backToSurvey(orderToken: string): Promise<Order> {
        console.log(`Navigating back to survey for order ${orderToken}`);
        try {
            const order = await this.put<Order>(`/orders/${orderToken}/back_to_survey`, {});
            console.log('Order state updated back to survey:', order);
            return order;
        } catch (error) {
            console.error(`API Error navigating back to survey for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Update guest count
    async updateGuests(orderToken: string, guestCount: number): Promise<Order> {
        console.log(`Updating guest count for order ${orderToken} to ${guestCount}`);
        try {
            const order = await this.put<Order>(
                `/orders/${orderToken}/guests`, 
                { guestCount }
            );
            console.log('Guest count updated, order:', order);
            return order;
        } catch (error) {
            console.error(`API Error updating guest count for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Add another registration to the order
    async addAnotherRegistration(orderToken: string): Promise<Order> {
        console.log(`Adding another registration to order ${orderToken}`);
        try {
            const order = await this.post<Order>(`/orders/${orderToken}/add_registration`, {});
            console.log('Registration added, order:', order);
            return order;
        } catch (error) {
            console.error(`API Error adding registration to order ${orderToken}:`, error);
            throw error;
        }
    }

    // Switch to a different registration
    async switchToRegistration(orderToken: string, registrationId: string): Promise<Order> {
        console.log(`Switching to registration ${registrationId} for order ${orderToken}`);
        try {
            const order = await this.post<Order>(
                `/orders/${orderToken}/switch_registration`, 
                { registrationId }
            );
            console.log('Switched to registration, order:', order);
            return order;
        } catch (error) {
            console.error(`API Error switching registration for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Check payment intent status
    async checkPaymentIntentStatus(orderToken: string): Promise<{
        paymentIntentId: string;
        status: string;
        amount: number;
        currency: string;
        lastPaymentError?: {
            code?: string;
            message?: string;
            type?: string;
        };
        orderState: string;
        orderCompleted: boolean;
    }> {
        console.log(`Checking payment intent status for order ${orderToken}`);
        try {
            const response = await this.get<any>(`/orders/${orderToken}/check_payment_intent_status`);
            console.log('Payment intent status:', response);
            return response;
        } catch (error) {
            console.error(`API Error checking payment intent status for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Update registration progress
    async updateRegistrationProgress(
        orderToken: string, 
        registrationId: string, 
        step: string
    ): Promise<EventRegistration> {
        console.log(`Updating progress for registration ${registrationId} to step ${step}`);
        try {
            const registration = await this.patch<EventRegistration>(
                `/orders/${orderToken}/registrations/${registrationId}/update_progress`, 
                { furthestValidStep: step }
            );
            console.log('Registration progress updated:', registration);
            return registration;
        } catch (error) {
            console.error(`API Error updating registration progress:`, error);
            throw error;
        }
    }

    // Remove a registration from the order
    async removeRegistration(orderToken: string, registrationId: string): Promise<Order> {
        console.log(`Removing registration ${registrationId} from order ${orderToken}`);
        try {
            const order = await this.delete<Order>(`/orders/${orderToken}/registrations/${registrationId}`);
            console.log('Registration removed, order:', order);
            return order;
        } catch (error) {
            console.error(`API Error removing registration from order ${orderToken}:`, error);
            throw error;
        }
    }

    // Reset participant type (clears tickets and returns to participant type selection)
    async resetParticipantType(orderToken: string): Promise<Order> {
        console.log(`Resetting participant type for order ${orderToken}`);
        try {
            const order = await this.post<Order>(`/orders/${orderToken}/reset_participant_type`, {});
            console.log('Participant type reset, order:', order);
            return order;
        } catch (error) {
            console.error(`API Error resetting participant type for order ${orderToken}:`, error);
            throw error;
        }
    }

    // Recover order after authentication - attempts to claim existing order or creates new one
    async recover(
        eventSlug: string, 
        orderToken: string, 
        participantTypeSlug: string
    ): Promise<Order> {
        console.log(`Recovering order ${orderToken} for event ${eventSlug} with participant type ${participantTypeSlug}`);
        try {
            const order = await this.post<Order>(
                `/orders/recover`,
                { 
                    eventSlug,
                    token: orderToken,
                    participantTypeSlug
                }
            );
            console.log('Order recovered successfully:', order);
            return order;
        } catch (error) {
            console.error(`API Error recovering order ${orderToken}:`, error);
            throw error;
        }
    }
}

export const registrationApi = new RegistrationApi();
